import React, { useMemo, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import { ParentSize } from '@visx/responsive';
import { Group } from '@visx/group';
import { scaleTime, scaleLinear } from '@visx/scale';
import { AreaClosed, LinePath, Bar, Line } from '@visx/shape';
import { LinearGradient } from '@visx/gradient';
import { GridRows } from '@visx/grid';
import { AxisBottom, AxisRight } from '@visx/axis';
import { curveMonotoneX } from '@visx/curve';
import { localPoint } from '@visx/event';
import { useTooltip, TooltipWithBounds, defaultStyles } from '@visx/tooltip';
import { bisector } from 'd3-array';
import { addCommas, formatValue, getFormatEntityN } from '../../../utils';
import { roundNum } from '../../../helpers';
import { convertDate } from '../../../helpers/time';

const ACCENT = '#0061FF'; // Modern blue
const AXIS_COLOR = '#8E8E93'; // Apple-style subtle gray
const GRID_COLOR = 'rgba(0, 0, 0, 0.04)';

const normalizePoint = (p) => {
  if (p == null) return null;
  if (Array.isArray(p)) {
    const d = new Date(p[0]);
    const v = Number(p[1]);
    return isNaN(d.getTime()) || isNaN(v) ? null : { date: d, value: v };
  }
  const rawDate = p.recordDate ?? p.date ?? p.x ?? p.t ?? p.dateFiled ?? p.label;
  const rawValue = p.value ?? p.y ?? p.v ?? p.close;
  const d = new Date(rawDate);
  const v = Number(rawValue);
  return isNaN(d.getTime()) || isNaN(v) ? null : { date: d, value: v };
};

const getDate = (d) => d.date;
const getValue = (d) => d.value;
const bisectDate = bisector((d) => d.date).left;

// Glassmorphism Tooltip
const tooltipStyles = {
  ...defaultStyles,
  background: 'rgba(28, 28, 35, 0.85)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  color: '#ffffff',
  border: '1px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '8px',
  padding: '10px 14px',
  fontSize: '13px',
  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
  lineHeight: 1.5,
  pointerEvents: 'none',
};

// Compact Formatter for the Y-Axis (e.g., 1,500,000 -> 1.5M)
const formatCompactAxis = (value) => {
  return '$' + new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    maximumFractionDigits: 1,
  }).format(value);
};

function ChartInner({
  width,
  height,
  data,
  entity,
  ariaLabel,
  lineColor,
  areaColor,
}) {
  const {
    showTooltip,
    hideTooltip,
    tooltipData,
    tooltipLeft = 0,
    tooltipTop = 0,
  } = useTooltip();

  // Margins updated to prevent clipping on left/right edges
  const isSmall = width < 480;
  const margin = useMemo(
    () => ({ top: 15, right: isSmall ? 45 : 55, bottom: 25, left: 20 }),
    [isSmall]
  );

  const innerWidth = Math.max(0, width - margin.left - margin.right);
  const innerHeight = Math.max(0, height - margin.top - margin.bottom);

  const xTickCount = Math.max(5, Math.floor(innerWidth / 90));
  const yTickCount = Math.max(5, Math.floor(innerHeight / 50));

  const xScale = useMemo(() => {
    const dates = data.map(getDate);
    return scaleTime({
      range: [0, innerWidth],
      domain: dates.length ? [dates[0], dates[dates.length - 1]] : [new Date(), new Date()],
    });
  }, [data, innerWidth]);

  const yScale = useMemo(() => {
    const values = data.map(getValue);
    const max = values.length ? Math.max(...values) : 1;
    const min = values.length ? Math.min(...values) : 0;

    // Add dynamic padding so the line doesn't hit the absolute top/bottom limits
    const padTop = (max - min) * 0.15 || max * 0.15;
    const padBottom = (max - min) * 0.05 || min * 0.05;

    return scaleLinear({
      range: [innerHeight, 0],
      domain: [Math.max(0, min - padBottom), max + padTop],
      nice: true,
    });
  }, [data, innerHeight]);

  const handleTooltip = useCallback(
    (event) => {
      if (!data.length) return;
      const point = localPoint(event);
      if (!point) return;

      const x = point.x - margin.left;

      // Ensure hover boundary is strictly inside the chart
      if (x < 0 || x > innerWidth) {
        hideTooltip();
        return;
      }

      const x0 = xScale.invert(x);
      const index = bisectDate(data, x0, 1);
      const d0 = data[index - 1];
      const d1 = data[index];
      let d = d0;
      if (d1 && getDate(d1)) {
        d = x0 - getDate(d0) > getDate(d1) - x0 ? d1 : d0;
      }
      if (!d) return;

      showTooltip({
        tooltipData: d,
        tooltipLeft: xScale(getDate(d)),
        tooltipTop: yScale(getValue(d)),
      });
    },
    [data, margin.left, innerWidth, xScale, yScale, showTooltip, hideTooltip]
  );

  if (width < 10 || !data.length) return null;

  // Exact Formatter for Tooltip
  const formatExact = (v) =>
    `$${addCommas(roundNum(formatValue(v, entity, true)))}${entity ? ` ${entity}` : ''}`;

  return (
    <Box sx={{ position: 'relative' }}>
      <svg width={width} height={height} role="img" aria-label={ariaLabel}>
        <LinearGradient
          id="fund-port-area"
          from={areaColor || lineColor}
          to={areaColor || lineColor}
          fromOpacity={0.25}
          toOpacity={0.0}
        />

        <Group left={margin.left} top={margin.top}>
          <GridRows
            scale={yScale}
            width={innerWidth}
            numTicks={yTickCount}
            stroke={GRID_COLOR}
          />

          <AreaClosed
            data={data}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getValue(d))}
            yScale={yScale}
            curve={curveMonotoneX}
            fill="url(#fund-port-area)"
          />

          <LinePath
            data={data}
            x={(d) => xScale(getDate(d))}
            y={(d) => yScale(getValue(d))}
            curve={curveMonotoneX}
            stroke={lineColor}
            strokeWidth={2.5}
          />

          <AxisRight
            left={innerWidth}
            scale={yScale}
            numTicks={yTickCount}
            stroke="transparent"
            tickStroke="transparent"
            tickFormat={(v) => formatCompactAxis(v)}
            tickLabelProps={() => ({
              fill: AXIS_COLOR,
              fontSize: 11,
              fontWeight: 500,
              textAnchor: 'start',
              dx: 8,
              dy: 3,
            })}
          />

          <AxisBottom
            top={innerHeight}
            scale={xScale}
            numTicks={xTickCount}
            stroke="transparent"
            tickStroke="transparent"
            tickFormat={(value) => convertDate(new Date(value), isSmall ? 'MMM yy' : "MMM 'yy")}
            tickLabelProps={() => ({
              fill: AXIS_COLOR,
              fontSize: 11,
              fontWeight: 500,
              textAnchor: 'middle',
              dy: 4,
            })}
          />

          <Bar
            x={0}
            y={0}
            width={innerWidth}
            height={innerHeight}
            fill="transparent"
            onMouseMove={handleTooltip}
            onMouseLeave={hideTooltip}
            onTouchMove={handleTooltip}
            onTouchStart={handleTooltip}
            onTouchEnd={hideTooltip}
          />

          {tooltipData && (
            <Group>
              <Line
                from={{ x: tooltipLeft, y: 0 }}
                to={{ x: tooltipLeft, y: innerHeight }}
                stroke={AXIS_COLOR}
                strokeWidth={1}
                strokeDasharray="4,4"
                pointerEvents="none"
              />
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={6}
                fill={lineColor}
                fillOpacity={0.2}
                pointerEvents="none"
              />
              <circle
                cx={tooltipLeft}
                cy={tooltipTop}
                r={4}
                fill={lineColor}
                stroke="#fff"
                strokeWidth={2}
                pointerEvents="none"
              />
            </Group>
          )}
        </Group>
      </svg>

      {tooltipData && (
        <TooltipWithBounds
          top={tooltipTop + margin.top}
          left={tooltipLeft + margin.left}
          style={tooltipStyles}
        >
          <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '2px' }}>
            {formatExact(getValue(tooltipData))}
          </div>
          <div style={{ opacity: 0.7, fontSize: '11px', fontWeight: 500 }}>
            {convertDate(getDate(tooltipData), 'MMM dd, yyyy')}
          </div>
        </TooltipWithBounds>
      )}
    </Box>
  );
}

function FundPortfolioValueChart({ seriesData, seriesStyle, height = 260, title, latestDate }) {
  const data = useMemo(() => {
    if (!Array.isArray(seriesData)) return [];
    return seriesData
      .map(normalizePoint)
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);
  }, [seriesData]);

  const lineColor = seriesStyle?.line?.stroke || ACCENT;
  const areaColor = seriesStyle?.area?.fill || lineColor;

  const latest = data.length ? data[data.length - 1] : null;
  const entity = latest ? getFormatEntityN(latest.value, true) : undefined;

  const latestLabel = latest
    ? `$${addCommas(roundNum(formatValue(latest.value, entity, true)))}`
    : '—';

  return (
    <Box sx={{ width: '100%', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          // justifyContent: 'space-between',
          justifyContent: 'start',
          flexWrap: 'wrap',
          mb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline' }}>
          <Typography
            component="span"
            sx={{ color: '#111827', fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em' }}
          >
            {latestLabel}
          </Typography>
          {entity && (
            <Typography
              component="span"
              sx={{ color: '#6B7280', fontSize: '.87rem', fontWeight: 600, ml: 0.5 }}
            >
              {entity}
            </Typography>
          )}
        </Box>
        {(latestDate || latest) && (
          <Typography
            component="span"
            sx={{ color: '#8E8E93', fontSize: '0.85rem', fontWeight: 500 }}
          >
            &nbsp; as of {convertDate(latestDate ?? latest.date, 'MMM dd, yyyy')}
          </Typography>
        )}
      </Box>

      {data.length === 0 ? (
        <Box
          sx={{
            height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8E8E93',
            fontSize: '0.9rem',
          }}
        >
          No portfolio value data available
        </Box>
      ) : (
        <Box sx={{ width: '100%', height }}>
          <ParentSize>
            {({ width }) => (
              <ChartInner
                width={width}
                height={height}
                data={data}
                entity={entity}
                ariaLabel={title || 'Portfolio value over time'}
                lineColor={lineColor}
                areaColor={areaColor}
              />
            )}
          </ParentSize>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(FundPortfolioValueChart);
